<template>
    <!-- Scan Serial Number Modal -->
    <b-modal
            body-class="d-flex flex-column py-0"
            id="part-serial-no-scan-modal"
            no-close-on-backdrop
            no-close-on-esc
            title="Add part">

        <!-- Header close button -->
        <template v-slot:modal-header-close>
            <span @click="closeModal">×</span>
        </template>

        <!----------------------------
          Can Scan
        ------------------------------>
        <template v-if="canScan">

            <!-- Barcode picker -->
            <div class="row mb-3">
                <barcode-picker @barcodeHasBeenScanned="barcodeScanned($event)" :inModal="true" class="d-md-none"></barcode-picker>
            </div>

            <ul class="timeline-list mb-0">

                <!----------------------------
                  WeFix Serial Number
                ------------------------------>
                <li>
                    <dl>
                        <dt>WeFix stock serial number</dt>
                        <dd>{{ barcode.id }}</dd><!-- TODO: Display the stock serial number - is this correct? -->
                    </dl>
                </li>

                <!----------------------------
                  New Part Serial Number
                ------------------------------>
                <!-- If the new part number isn't scanned -->
                <li class="current" v-if="!newPartScanned">

                    <!-- Empty State -->
                    <empty-state
                            align="left"
                            class="p-0"
                            icon="scanner"
                            inline
                            small
                            subtitle="Scan the serial number of the NEW PART"
                            title="">
                    </empty-state>
                    <button class="btn btn-danger btn-sm d-none d-md-inline-block mt-2" @click="cantScanNewSerial">Can't scan serial number</button>
                </li>

                <!-- If the new part number is scanned -->
                <li v-if="newPartScanned">
                    <dl>
                        <dt>New part serial number</dt>
                        <dd>
                            <template v-if="newPartScanned !== 'CNS'">{{ newPartScanned }}</template><template v-if="newPartScanned === 'CNS'">Could not scan</template>
                            <button class="btn btn-link btn-sm" @click="editNewPartSerial">Edit</button>
                        </dd>
                    </dl>
                </li>

                <!----------------------------
                  Old Part Serial Number
                ------------------------------>
                <!-- If the new part number isn't scanned -->
                <li class="disabled" v-if="(newPartScanned === null && !oldPartScanned) || (editingNewPartSerial && !oldPartScanned)">
                    <dl>
                        <dt>Old part serial number</dt>
                        <dd>Scan new part serial first</dd>
                    </dl>
                </li>

                <!-- If the new part number is scanned but the old part isn't -->
                <li class="current" v-if="newPartScanned !== null && !oldPartScanned && !editingNewPartSerial">

                    <!-- Empty State -->
                    <empty-state
                            align="left"
                            class="p-0"
                            icon="scanner"
                            inline
                            small
                            subtitle="Scan the serial number of the OLD PART"
                            title="">
                    </empty-state>
                    <button class="btn btn-danger btn-sm d-none d-md-inline-block mt-2" @click="cantScanOldSerial">Can't scan serial number</button>
                </li>

                <!-- If the old part number is scanned -->
                <li v-if="(newPartScanned !== null && oldPartScanned) || (editingNewPartSerial && oldPartScanned)">
                    <dl>
                        <dt>Old part serial number</dt>
                        <dd>
                            <template v-if="oldPartScanned !== 'CNS'">{{ oldPartScanned }}</template><template v-if="oldPartScanned === 'CNS'">Could not scan</template>
                            <button class="btn btn-link btn-sm" @click="oldPartScanned = false">Edit</button>
                        </dd>
                    </dl>
                </li>

            </ul>

            <div class="mb-4 mt-auto row d-md-none">
                <div class="col-12 text-center">
                    <button class="btn btn-info btn-sm" @click="canScan = false">Issues scanning?</button>
                </div>
            </div>

        </template>

        <!----------------------------
          Can't Scan
        ------------------------------>
        <template v-if="!canScan">

            <ul class="timeline-list mb-0">
                <li>
                    <h4 class="mb-2">Step 1</h4>
                    <p>
                        On your computer go to www.app.wefix.co.uk/jobs/{{jobId}}/repair/{{deviceId}}
                    </p>
                </li>
                <li>
                    <h4 class="mb-2">Step 2</h4>
                    <p>
                        Use your Honeywell handheld scanner to first scan the part, then the old part serial number, followed by the new part serial number
                    </p>
                </li>
                <li>
                    <h4 class="mb-2">Step 3</h4>
                    <p>
                        When both serial numbers have been scanned, you can continue the repair either on your computer or mobile
                    </p>
                </li>
            </ul>

            <div class="mb-4 mt-auto row">
                <div class="col-12 text-center">
                    <button class="btn btn-info btn-sm" @click="canScan = true">Back to scanner</button>
                </div>
            </div>
        </template>

        <template v-slot:modal-footer>
            <button type="button" class="btn btn-success ml-auto" :disabled="!newPartScanned || !oldPartScanned" @click="saveSerials()">Add part</button>
        </template>

    </b-modal>
</template>

<script>
    import {mapActions} from "vuex";

    export default {

        props:['barcode', 'deviceId', 'jobId'],

        data() {
            return {
                canScan: true,
                editingNewPartSerial: false,
                newPartScanned: null,
                oldPartScanned: null,
            }
        },

        mounted() {
            this.canScan = true;
        },

        methods: {
            ...mapActions([
                "destroyBarcodeScanner",
                "displayToast"
            ]),

            /** Barcode scanned */
            barcodeScanned(barcode) {
                let barcodeData = barcode;

                if (barcode.data) {
                    barcodeData = barcode.data;
                }

                if (typeof barcodeData !== 'string') {
                    return this.displayToast({text: 'Invalid serial number. Please ensure you scan the part serial number', type: 'danger'});
                }

                if (!this.newPartScanned) {
                    if (barcodeData === this.oldPartScanned) {
                        return this.displayToast({text: 'This is the OLD part serial number. Please scan the NEW part serial number', type: 'danger'});
                    }

                    this.newPartScanned = barcodeData
                    this.editingNewPartSerial = false
                    return;
                }

                if (this.newPartScanned && !this.oldPartScanned) {
                    if (barcodeData === this.newPartScanned) {
                        return this.displayToast({text: 'This is the NEW part serial number. Please scan the OLD part serial number', type: 'danger'});
                    }

                    this.oldPartScanned = barcodeData;
                    this.canScan = true;
                }
            },

            /** Can't scan the new part serial */
            cantScanNewSerial() {
                this.newPartScanned = "CNS"
            },

            /** Can't scan the old part serial */
            cantScanOldSerial() {
                this.oldPartScanned = "CNS";
            },

            /** Close the modal */
            closeModal() {
                this.canScan = true;
                this.destroyBarcodeScanner()
                    .then(() => {
                        this.$bvModal.hide('part-serial-no-scan-modal');
                        this.$emit('close');
                    });
            },

            editNewPartSerial() {
                this.newPartScanned = false;
                this.editingNewPartSerial = true;
            },

            /** Save the serial numbers */
            saveSerials() {
                this.$emit('serialsScanned', {
                    newSerial: this.newPartScanned,
                    oldSerial: this.oldPartScanned,
                });
                this.closeModal();
            }

        }
    }
</script>

<style scoped>

</style>